// Boîte du support (D-082), pour l'équipe Arceau (admins de la plateforme, D-081) seulement :
//   #/support           les messages de tous les clubs, filtrés par état ; les avis « 💬 Ton avis » (D-103)
//                       par sujet, « autres clubs » et page, avec le décompte de la semaine
//   #/support/<id>      un message : contexte, capture, réponse et état (l'auteur est prévenu d'une réponse, et
//                       quand le message passe « Traité »)
// La tâche de traitement automatique passe par le serveur (`sudo arceau support …`), pas par ici.
import {
  AVIS_TOPICS, OTHERS_LABEL, TOPIC_LABEL, TOPIC_SHORT, browserLabel, isOpen, KIND_LABEL, pageName, STATUS_ADMIN, STATUS_CHIP, STATUSES,
  type AvisOthers, type AvisTopic, type SupportStatus,
} from '../help/support';
import { isPlatformAdmin, supportInbox, supportReply, supportScreenshot, type InboxRow } from '../lib/support';
import { avisMeta, avisWeekly, type AvisWeek } from '../lib/feedback';
import type { Ctx } from './context';
import { $, $$, html, mount, toast } from './dom';
import './feedback.css';

type Filter = 'open' | SupportStatus | 'all';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'open', label: 'À traiter' }, { key: 'new', label: 'Nouveaux' }, { key: 'test', label: 'En test' },
  { key: 'done', label: 'Traités' }, { key: 'wontfix', label: 'Pas retenus' }, { key: 'all', label: 'Tous' },
];
/** Sujet : tous les messages, les seuls messages au support (sans sujet), ou un sujet d'avis. */
type TopicFilter = 'all' | 'support' | AvisTopic;
let filter: Filter = 'open';
let topicF: TopicFilter = 'all';
let othersF = false;
let pageF = '';

const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
const keep = (r: InboxRow, f: Filter) => (f === 'all' ? true : f === 'open' ? isOpen(r.status) : r.status === f);
const who = (r: InboxRow) => r.author_name || r.author_email || 'Compte supprimé';
/** La page d'un message, en mots, sans l'équipe : celle que l'auteur a vue (avis), sinon d'après l'adresse. */
const pageOfRow = (r: InboxRow) => (r.page_label ? r.page_label.split(' · ')[0]!.trim() : r.page?.startsWith('#') ? pageName(r.page) : r.page ? 'Autre page' : '—');
const keepTopic = (r: InboxRow, t: TopicFilter) => (t === 'all' ? true : t === 'support' ? !r.topic : r.topic === t);
const kindChip = (r: InboxRow) => (r.topic
  ? html`<span class="sp-kind sp-topic" title="Ton avis · ${TOPIC_LABEL[r.topic]}">${TOPIC_SHORT[r.topic]}</span>`
  : html`<span class="sp-kind sp-${r.kind}">${KIND_LABEL[r.kind]}</span>`);
const mondayOf = (d: Date) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

export async function renderSupportInbox(root: HTMLElement, ctx: Ctx, id?: string): Promise<void> {
  if (!(await isPlatformAdmin(ctx.uid))) {
    mount(root, html`<div class="empty"><p class="empty-title">Cette page n’existe pas.</p><a class="btn btn-ghost" href="#/aide">Aide</a></div>`);
    return;
  }
  const [rows, meta, weeks] = await Promise.all([
    supportInbox(), avisMeta().catch(() => []), id ? Promise.resolve([] as AvisWeek[]) : avisWeekly(8).catch(() => [] as AvisWeek[]),
  ]);
  const byId = new Map(meta.map(m => [m.id, m]));
  for (const r of rows) { const m = byId.get(r.id); if (m) { r.topic = m.topic; r.for_other_clubs = m.for_other_clubs; r.page_label = m.page_label; } }
  const row = id ? rows.find(r => r.id === id) : undefined;
  if (row) return renderMessage(root, ctx, row);

  const pages = [...new Set(rows.filter(r => r.topic).map(pageOfRow))].sort((a, b) => a.localeCompare(b, 'fr'));
  if (pageF && !pages.includes(pageF)) pageF = '';
  const draw = () => {
    const other = (r: InboxRow) => (!othersF || r.for_other_clubs === 'yes') && (!pageF || pageOfRow(r) === pageF);
    const base = rows.filter(r => keepTopic(r, topicF) && other(r));
    const shown = base.filter(r => keep(r, filter));
    const topics: { key: TopicFilter; label: string }[] = [
      { key: 'all', label: 'Tous' }, ...AVIS_TOPICS.map(t => ({ key: t as TopicFilter, label: `💬 ${TOPIC_SHORT[t]}` })), { key: 'support', label: 'Support' },
    ];
    const nTopic = (t: TopicFilter) => rows.filter(r => keep(r, filter) && keepTopic(r, t) && other(r)).length;
    mount(root, html`
      <section class="screen sp">
        <a class="back" href="#/aide" data-back>‹ Aide</a>
        <header class="screen-head"><p class="eyebrow">Équipe Arceau</p><h2>Support</h2></header>
        ${weeklyBlock(weeks)}
        <div class="chip-toggles sp-filters" role="group" aria-label="État">${FILTERS.map(f => html`
          <button type="button" class="chip-toggle" data-f="${f.key}" aria-pressed="${String(filter === f.key)}">${f.label} <small>${base.filter(r => keep(r, f.key)).length}</small></button>`)}</div>
        ${meta.length ? html`
          <div class="chip-toggles sp-filters" role="group" aria-label="Sujet">${topics.map(t => html`
            <button type="button" class="chip-toggle" data-t="${t.key}" aria-pressed="${String(topicF === t.key)}">${t.label} <small>${nTopic(t.key)}</small></button>`)}
            <button type="button" class="chip-toggle" id="sp-others" aria-pressed="${String(othersF)}">Utile à d’autres clubs</button></div>
          ${pages.length ? html`<label class="field sp-page"><span>Page</span><select id="sp-pagef"><option value="">Toutes les pages</option>${pages.map(p => html`
            <option value="${p}" ${p === pageF ? 'selected' : ''}>${p}</option>`)}</select></label>` : ''}` : ''}
        ${shown.length ? html`<ul class="list sp-list">${shown.map(r => html`
          <li><a href="#/support/${r.id}">
            ${kindChip(r)}
            <span class="card-main">
              <span class="sp-who"><strong>${who(r)}</strong>${r.club_name ? ` · ${r.club_name}` : ''}</span>
              <span class="sp-body">${r.body}</span>
              <span class="muted small">${when(r.created_at)}${r.topic ? ` · ${pageOfRow(r)}` : r.casquette ? ` · ${r.casquette}` : ''}${r.has_screenshot ? ' · capture' : ''}${r.reply ? ' · répondu' : ''}${r.for_other_clubs === 'yes' ? html` <span class="sp-others">· autres clubs</span>` : ''}</span>
            </span>
            <span class="chip ${STATUS_CHIP[r.status]}">${STATUS_ADMIN[r.status]}</span>
          </a></li>`)}</ul>`
          : html`<div class="empty"><p class="empty-title">Rien ici.</p><p>Les messages des clubs et les avis des clubs pilotes arrivent dans cette boîte, avec une notification.</p></div>`}
        <p class="hint">La tâche de traitement lit et met à jour ces messages sur le serveur : <span class="mono">sudo arceau support list</span> ; le point de la semaine : <span class="mono">sudo arceau support digest</span>.</p>
      </section>`);
    for (const b of $$<HTMLButtonElement>(root, '[data-f]')) b.addEventListener('click', () => { filter = b.dataset.f as Filter; draw(); });
    for (const b of $$<HTMLButtonElement>(root, '[data-t]')) b.addEventListener('click', () => { topicF = b.dataset.t as TopicFilter; draw(); });
    $(root, '#sp-others')?.addEventListener('click', () => { othersF = !othersF; draw(); });
    $<HTMLSelectElement>(root, '#sp-pagef')?.addEventListener('change', (e) => { pageF = (e.target as HTMLSelectElement).value; draw(); });
  };
  draw();
}

/** Les avis de la semaine : par sujet, les pages qui reviennent, « utile à d'autres clubs » ; la semaine d'avant en chiffre. */
function weeklyBlock(weeks: AvisWeek[]) {
  if (!weeks.length) return '';
  const monday = mondayOf(new Date());
  const cur = ymd(monday), prev = ymd(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7));
  const now = weeks.filter(x => x.week === cur), before = weeks.filter(x => x.week === prev);
  const sum = (l: AvisWeek[], k: 'messages' | 'for_other_clubs') => l.reduce((n, x) => n + x[k], 0);
  const byTopic = AVIS_TOPICS.map(t => [t, now.filter(x => x.topic === t).reduce((n, x) => n + x.messages, 0)] as const).filter(([, n]) => n);
  const pages = [...now.reduce((m, x) => m.set(x.page, (m.get(x.page) ?? 0) + x.messages), new Map<string, number>())].sort((a, b) => b[1] - a[1]).slice(0, 4);
  const n = sum(now, 'messages'), o = sum(now, 'for_other_clubs');
  return html`
    <section class="block sp-avis">
      <div class="block-head"><h3>Ton avis · semaine du ${dayLabel(cur)}</h3><span class="muted small">${n} avis${before.length ? ` · ${sum(before, 'messages')} la semaine d’avant` : ''}</span></div>
      ${byTopic.length ? html`<div class="sp-avis-week">${byTopic.map(([t, k]) => html`<span class="chip chip-soft">${TOPIC_SHORT[t]} · ${k}</span>`)}
        ${o ? html`<span class="chip chip-ok">Utile à d’autres clubs · ${o}</span>` : ''}</div>
        <ul class="sp-avis-pages">${pages.map(([p, k]) => html`<li><span>${p}</span><b>${k}</b></li>`)}</ul>`
        : html`<p class="muted small">Pas encore d’avis cette semaine.</p>`}
    </section>`;
}

async function renderMessage(root: HTMLElement, ctx: Ctx, r: InboxRow): Promise<void> {
  // Ouvert par l'équipe : « Nouveau » devient « Vu ».
  if (r.status === 'new') { try { await supportReply(r.id, 'seen', null); r.status = 'seen'; } catch { /* reste « Nouveau » */ } }
  const others = (o: AvisOthers | null | undefined) => (o ? OTHERS_LABEL[o] : 'pas de réponse');
  mount(root, html`
    <section class="screen sp">
      <a class="back" href="#/support" data-back>‹ Support</a>
      <header class="screen-head"><p class="eyebrow">${r.topic ? `💬 Ton avis · ${TOPIC_LABEL[r.topic]}` : KIND_LABEL[r.kind]} · ${when(r.created_at)}</p><h2>${who(r)}</h2></header>
      <section class="block"><div class="block-head"><h3>Message</h3><span class="chip ${STATUS_CHIP[r.status]}">${STATUS_ADMIN[r.status]}</span></div>
        <p class="note">${r.body}</p></section>
      <section class="block sp-ctx"><h3>Contexte</h3>
        <dl>
          ${r.topic ? html`<dt>Avis</dt><dd>${TOPIC_LABEL[r.topic]} <span class="muted">(${KIND_LABEL[r.kind]})</span></dd>
            <dt>Autres clubs</dt><dd>${others(r.for_other_clubs)}</dd>` : ''}
          <dt>Auteur</dt><dd>${who(r)}${r.author_email && r.author_name ? html` <span class="muted">${r.author_email}</span>` : ''}</dd>
          <dt>Club</dt><dd>${r.club_name ?? '—'}</dd>
          <dt>Casquette</dt><dd>${r.casquette ?? '—'}</dd>
          <dt>Page</dt><dd>${r.page_label ? html`${r.page_label}<br>` : ''}<span class="mono">${r.page ?? '—'}</span></dd>
          <dt>Version</dt><dd class="mono">${r.app_version ?? '—'}</dd>
          <dt>Navigateur</dt><dd>${r.user_agent ? browserLabel(r.user_agent) : '—'}${r.user_agent ? html`<br><span class="muted small">${r.user_agent}</span>` : ''}</dd>
          <dt>Identifiant</dt><dd class="mono small">${r.id}</dd>
        </dl></section>
      ${r.has_screenshot ? html`<section class="block"><h3>Capture</h3><div id="sp-img"><button type="button" class="btn btn-ghost btn-sm" id="sp-load">Voir la capture</button></div></section>` : ''}
      <section class="block"><h3>Réponse</h3>
        <form class="form" id="f-reply" novalidate>
          <label class="field"><span>État</span><select name="status">${STATUSES.map(s => html`<option value="${s}" ${s === r.status ? 'selected' : ''}>${STATUS_ADMIN[s]}</option>`)}</select></label>
          <label class="field"><span>Réponse à l’auteur (il la lit dans « Mes messages »)</span>
            <textarea name="reply" rows="5" maxlength="4000" placeholder="Merci ! …">${r.reply ?? ''}</textarea></label>
          ${r.replied_at ? html`<p class="hint">Répondu le ${when(r.replied_at)}.</p>` : ''}
          <button class="btn btn-primary" type="submit">Enregistrer</button>
          <p class="hint">Une réponse nouvelle ou modifiée prévient l’auteur par une notification ; le passage à « Traité » aussi.</p>
        </form></section>
    </section>`);
  $(root, '#sp-load')?.addEventListener('click', async () => {
    const box = $(root, '#sp-img')!;
    try {
      const src = await supportScreenshot(r.id);
      mount(box, src ? html`<img class="sp-shot" src="${src}" alt="Capture jointe au message">` : html`<p class="muted">Plus de capture.</p>`);
    } catch { toast('Capture illisible.', 'ko'); }
  });
  const f = $<HTMLFormElement>(root, '#f-reply')!;
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(f);
    const status = String(fd.get('status')) as SupportStatus;
    const text = String(fd.get('reply') ?? '').trim();
    const reply = text === (r.reply ?? '') ? null : text;   // null : réponse inchangée ; '' : retirée
    try {
      await supportReply(r.id, status, reply);
      toast(reply ? 'Réponse envoyée : l’auteur est prévenu' : status === 'done' && r.status !== 'done' ? 'Traité : l’auteur est prévenu' : 'Enregistré');
      void renderSupportInbox(root, ctx, r.id);
    } catch { toast('Enregistrement impossible.', 'ko'); }
  });
}
